/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/st_buffer.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * Computes a buffer area around the input geometry at the specified distance. The distance is in the units of the input spatial reference system. Positive distances expand the geometry, negative distances shrink it. A distance of zero will return the input geometry unchanged. Points and lines become polygons when buffered, unless a zero distance is provided.
 *
 * @example
 * ROW wkt = "POLYGON ((0 0, 4 0, 4 4, 0 4, 0 0))"
 * | EVAL buffered = ST_BUFFER(TO_GEOSHAPE(wkt), -1)
 *
 * @example
 * ROW geom = TO_CARTESIANSHAPE("LINESTRING(0 0, 10 0, 10 10)")
 * | EVAL buffered = ST_BUFFER(geom, 2, {"endcap": "flat", "join": "mitre"})
 * | KEEP buffered
 */
const definition: FunctionDefinition = {
  type: 'scalar',
  name: 'st_buffer',
  signatures: [
    {
      params: [
        {
          name: 'geometry',
          type: 'cartesian_point',
          optional: false,
        },
        {
          name: 'distance',
          type: 'double',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'cartesian_shape',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'cartesian_point',
          optional: false,
        },
        {
          name: 'distance',
          type: 'float',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'cartesian_shape',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'cartesian_point',
          optional: false,
        },
        {
          name: 'distance',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'cartesian_shape',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'cartesian_point',
          optional: false,
        },
        {
          name: 'distance',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'cartesian_shape',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'cartesian_shape',
          optional: false,
        },
        {
          name: 'distance',
          type: 'double',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'cartesian_shape',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'cartesian_shape',
          optional: false,
        },
        {
          name: 'distance',
          type: 'float',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'cartesian_shape',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'cartesian_shape',
          optional: false,
        },
        {
          name: 'distance',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'cartesian_shape',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'cartesian_shape',
          optional: false,
        },
        {
          name: 'distance',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'cartesian_shape',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'geo_point',
          optional: false,
        },
        {
          name: 'distance',
          type: 'double',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'geo_shape',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'geo_point',
          optional: false,
        },
        {
          name: 'distance',
          type: 'float',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'geo_shape',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'geo_point',
          optional: false,
        },
        {
          name: 'distance',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'geo_shape',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'geo_point',
          optional: false,
        },
        {
          name: 'distance',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'geo_shape',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'geo_shape',
          optional: false,
        },
        {
          name: 'distance',
          type: 'double',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'geo_shape',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'geo_shape',
          optional: false,
        },
        {
          name: 'distance',
          type: 'float',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'geo_shape',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'geo_shape',
          optional: false,
        },
        {
          name: 'distance',
          type: 'integer',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'geo_shape',
    },
    {
      params: [
        {
          name: 'geometry',
          type: 'geo_shape',
          optional: false,
        },
        {
          name: 'distance',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'geo_shape',
    },
  ],
  preview: true,
  snapshotOnly: false,
};

export default definition;
